import React, {useState} from 'react';
import {Button, Modal} from 'semantic-ui-react';
import {useViewer} from '../state/viewer/hooks';
import {useSelector, useApolloClient} from '../state/hooks';
import * as Urls from '../util/urls';
import {isOnReportView} from '../util/url';
import {ReportViewRef} from '../state/reports/types';
import * as ViewApi from '../state/views/api';
import ReactTimeago from 'react-timeago';
import {useHistory} from 'react-router';
import * as ViewSelectors from '../state/views/selectors';
import * as ViewNormalize from '../state/views/normalize';
import {LoadResultType as LoadedView} from '../state/views/api';
import {
  getProjectSpecifierKey,
  getProjectSpecifierMap,
  useReportProjects,
} from '../util/report';
import {useUpdateAccessTokenProjectsMutation} from '../generated/graphql';
import makeComp from '../util/profiler';

interface SaveDraftTriggerProps {
  viewRef: ReportViewRef;
}

const SaveDraftTrigger: React.FC<SaveDraftTriggerProps> = makeComp(
  props => {
    const viewer = useViewer();
    const client = useApolloClient();
    const history = useHistory();
    const view = useSelector(state => state.views.views[props.viewRef.id]);
    const views = useSelector(ViewSelectors.getViews);
    const reportID = view.id;
    if (reportID == null) {
      throw new Error('invalid state');
    }
    const viewPartRef = view.partRef;
    if (viewPartRef == null) {
      throw new Error('View in redux missing part ref');
    }
    const [warningOpen, setWarningOpen] = useState(false);
    const [parent, setParent] = useState<LoadedView | null>(null);

    const [updateAccessTokenProjects] = useUpdateAccessTokenProjectsMutation();

    const projectsIncluded = useReportProjects(props.viewRef);

    const needAccessTokenUpdate = (parentView: LoadedView): boolean => {
      if (
        parentView.accessTokens == null ||
        parentView.accessTokens.length === 0
      ) {
        return false;
      }
      const accessTokenProjects = parentView.accessTokens[0].projects;
      if (projectsIncluded.length !== accessTokenProjects.length) {
        return true;
      }

      const projectSpecifierMap = getProjectSpecifierMap(projectsIncluded);
      return accessTokenProjects.some(
        p =>
          projectSpecifierMap[
            getProjectSpecifierKey({
              entityName: p.entityName,
              projectName: p.name,
            })
          ] == null
      );
    };

    if (viewer == null) {
      return <>{props.children}</>;
    }

    const saveToMaster = async (parentView: LoadedView) => {
      await ViewApi.save(client, {
        id: view.parentId,
        type: 'runs',
        displayName: view.displayName,
        description: view.description,
        spec: ViewNormalize.denormalize(views.parts, viewPartRef),
        project: view.project,
        previewUrl: view.previewUrl,
        coverUrl: view.coverUrl,
      });
      await ViewApi.deleteView(client, reportID);

      if (needAccessTokenUpdate(parentView)) {
        const token = parentView.accessTokens?.[0]?.token;
        if (token != null) {
          await updateAccessTokenProjects({
            variables: {
              token,
              projects: projectsIncluded,
            },
          });
        }
      }

      history.push(
        Urls.reportView({
          entityName: view.project.entityName,
          projectName: view.project.name,
          reportID: view.parentId as string,
        })
      );
    };

    return (
      <>
        <div
          style={{display: 'contents'}}
          onClick={async () => {
            if (view.parentId == null) {
              await ViewApi.save(client, {
                id: view.id,
                type: 'runs',
                displayName: view.displayName,
                description: view.description,
                spec: ViewNormalize.denormalize(views.parts, viewPartRef),
                project: view.project,
                previewUrl: view.previewUrl,
                coverUrl: view.coverUrl,
              });
              if (isOnReportView()) {
                window.location.reload();
              } else {
                history.push(
                  Urls.reportView({
                    entityName: view.project.entityName,
                    projectName: view.project.name,
                    reportID,
                  })
                );
              }
            } else {
              const loadedParent = await ViewApi.load(client, view.parentId);
              setParent(loadedParent);
              if (loadedParent.updatedAt > view.createdAt) {
                setWarningOpen(true);
              } else {
                saveToMaster(loadedParent);
              }
            }
          }}>
          {props.children}
        </div>
        {parent != null && warningOpen && (
          <Modal
            size="mini"
            open={true}
            onClose={() => {
              setWarningOpen(false);
            }}>
            <Modal.Content>
              <h3>Potential conflict</h3>
              <p style={{marginBottom: 24}}>
                This report was modified{' '}
                <ReactTimeago date={parent.updatedAt + 'Z'} live={false} /> by{' '}
                {parent.updatedBy?.username}.{' '}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href={Urls.reportView({
                    entityName: view.project.entityName,
                    projectName: view.project.name,
                    reportID: view.parentId as string,
                  })}>
                  View the report
                </a>{' '}
                to see their changes and potentially resolve conflicts.
              </p>
              <Button
                color="red"
                size="tiny"
                onClick={() => saveToMaster(parent)}>
                Overwrite changes
              </Button>
              <Button size="tiny" onClick={() => setWarningOpen(false)}>
                Cancel
              </Button>
            </Modal.Content>
          </Modal>
        )}
      </>
    );
  },
  {id: 'SaveDraftTrigger'}
);

export default SaveDraftTrigger;
